// Derivaciones puras sobre `LoanInstallmentResponse`: saldo insoluto, mora, próxima cuota y último
// pago. Viven aquí y no en un dominio porque el DTO es de `@fiado/type-kit` y lo leen varias
// pantallas —la ficha de crédito y la cartera del portal del vendedor— con la misma semántica.
import { LoanInstallmentStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanInstallmentStatusEnum';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { daysBetweenDays, mxDay } from './dateHelpers';

/** Cuotas ya cobradas: son las que cuentan como cupón pagado. */
export const PAID_INSTALLMENT_STATUSES: readonly LoanInstallmentStatusEnum[] = [
    LoanInstallmentStatusEnum.PAID,
    LoanInstallmentStatusEnum.PAID_IN_ADVANCE,
];

/** Cuotas que ya no se deben. La reestructurada dejó de aplicar aunque nadie la haya pagado. */
export const CLEARED_INSTALLMENT_STATUSES: readonly LoanInstallmentStatusEnum[] = [
    ...PAID_INSTALLMENT_STATUSES,
    LoanInstallmentStatusEnum.RESTRUCTURED,
];

export function isPaidInstallment(status: LoanInstallmentStatusEnum): boolean {
    return PAID_INSTALLMENT_STATUSES.includes(status);
}

export function isClearedInstallment(status: LoanInstallmentStatusEnum): boolean {
    return CLEARED_INSTALLMENT_STATUSES.includes(status);
}

export function paidInstallmentsCountOf(installments: readonly LoanInstallmentResponse[]): number {
    return installments.filter((item) => isPaidInstallment(item.status)).length;
}

/** Saldo insoluto: el capital de las cuotas que todavía se deben. */
export function outstandingBalanceCentsOf(
    installments: readonly LoanInstallmentResponse[],
): number {
    return installments
        .filter((item) => !isClearedInstallment(item.status))
        .reduce((total, item) => total + item.capitalCents, 0);
}

/** La primera cuota que todavía se debe, en orden de plan. */
export function nextDueInstallment(
    installments: readonly LoanInstallmentResponse[],
): LoanInstallmentResponse | null {
    return (
        [...installments]
            .filter((item) => !isClearedInstallment(item.status))
            .sort((a, b) => a.installmentNumber - b.installmentNumber)[0] ?? null
    );
}

/** La cuota cobrada más recientemente. `null` es «todavía no hay pagos», nunca «no se sabe». */
export function lastPaidInstallment(
    installments: readonly LoanInstallmentResponse[],
): LoanInstallmentResponse | null {
    return (
        [...installments]
            .filter((item) => isPaidInstallment(item.status) && item.paidAt)
            .sort((a, b) => (a.paidAt ?? '').localeCompare(b.paidAt ?? ''))
            .at(-1) ?? null
    );
}

/** El día de calendario de una cuota, venga como `YYYY-MM-DD` o como instante ISO completo. */
export function installmentDueDay(dueDate: string): string {
    return dueDate.length === 10 ? dueDate : mxDay(dueDate);
}

/**
 * Días de atraso de UNA cuota. `null` es que no atrasa —o que no hay vencimiento contra el que
 * medirlo—; una vencida hoy cuenta al menos un día para no leerse como al corriente.
 */
export function installmentArrearsDays(
    installment: LoanInstallmentResponse,
    today: string,
): number | null {
    if (isClearedInstallment(installment.status) || !installment.dueDate) return null;
    const elapsed = daysBetweenDays(today, installmentDueDay(installment.dueDate));
    if (installment.status === LoanInstallmentStatusEnum.OVERDUE) return Math.max(1, elapsed);
    return elapsed > 0 ? elapsed : null;
}

/**
 * Días de mora desde la cuota vencida más vieja. `0` es «al día» y `null` es «no hay de dónde
 * saberlo»: sin cuotas, o con alguna que se debe y no trae vencimiento.
 */
export function arrearsDaysOf(
    installments: readonly LoanInstallmentResponse[],
    today: string,
): number | null {
    if (installments.length === 0) return null;
    const owed = installments.filter((item) => !isClearedInstallment(item.status));
    const days: number[] = [];
    for (const item of owed) {
        if (!item.dueDate) return null;
        const arrears = installmentArrearsDays(item, today);
        if (arrears !== null) days.push(arrears);
    }
    return days.length === 0 ? 0 : Math.max(...days);
}

/** Lo aplicado a UNA cuota según su estado: el total si está pagada, si no cero. `null` = reestructurada. */
export function installmentPaidCents(installment: LoanInstallmentResponse): number | null {
    if (installment.status === LoanInstallmentStatusEnum.RESTRUCTURED) return null;
    return isPaidInstallment(installment.status) ? installment.totalCents : 0;
}

/** Lo cubierto de una cuota, parciales incluidos: total menos pendiente. `null` = reestructurada. */
export function installmentCoveredCents(installment: LoanInstallmentResponse): number | null {
    if (installment.status === LoanInstallmentStatusEnum.RESTRUCTURED) return null;
    return Math.max(0, installment.totalCents - installment.outstandingBalanceCents);
}
